import React, { useState, useRef, useEffect } from "react";
import { ChatSession } from "../../../services/api/chatHistory.api";
import { useClickOutside } from "../../../hooks/useClickOutside";
import FolderPickerPopover, { FolderOption } from "./FolderPickerPopover";

interface SessionItemProps {
  session: ChatSession;
  isActive: boolean;
  folders: FolderOption[];                                    // all available folders for the picker
  onClick: (sessionId: string) => void;
  onRename: (sessionId: string, newName: string) => void;
  onDelete: (sessionId: string) => void;
  onMoveToFolder: (sessionId: string, folderId: number) => void;
  onRemoveFromFolder?: (sessionId: string) => void;           // only passed when inside a folder
}

const SessionItem: React.FC<SessionItemProps> = ({
  session,
  isActive,
  folders,
  onClick,
  onRename,
  onDelete,
  onMoveToFolder,
  onRemoveFromFolder,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(session.chat_name);
  const [isFolderPickerOpen, setIsFolderPickerOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close context menu on outside click (the ⋯ button counts as inside, so it
  // can close the menu too)
  useClickOutside([menuRef, menuBtnRef], () => setMenuOpen(false), menuOpen);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing) inputRef.current?.select();
  }, [isEditing]);

  const commitRename = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== session.chat_name) {
      onRename(session.session_id, trimmed);
    } else {
      setEditValue(session.chat_name);
    }
    setIsEditing(false);
  };

  return (
    <div
      className={`chat-item ${isActive ? "active" : ""}`}
      onClick={() => !isEditing && onClick(session.session_id)}
      title={session.chat_name}
    >
      {isEditing ? (
        <input
          ref={inputRef}
          className="chat-item-rename-input"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") {
              setEditValue(session.chat_name);
              setIsEditing(false);
            }
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span>{session.chat_name}</span>
      )}

      {/* ⋯ context menu trigger */}
      <button
        ref={menuBtnRef}
        className="chat-item-menu-btn"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((o) => !o);
        }}
        title="More options"
      >
        <i className="fas fa-ellipsis-h" />
      </button>

      {menuOpen && (
        <div ref={menuRef} className="chat-item-dropdown" onClick={(e) => e.stopPropagation()}>

          {/* Rename */}
          <button
            className="menu-item"
            onClick={() => {
              setMenuOpen(false);
              setIsEditing(true);
            }}
          >
            <i className="fas fa-pencil" />
            <span>Rename</span>
          </button>

          {/* Move to folder — only shown when NOT already inside a folder */}
          {!onRemoveFromFolder && (
            <button
              className="menu-item"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(false);
                setIsFolderPickerOpen(true);
              }}
            >
              <i className="fas fa-folder-plus" />
              <span>Move to folder</span>
            </button>
          )}

          {/* Remove from folder — only shown when inside a folder */}
          {onRemoveFromFolder && (
            <button
              className="menu-item"
              onClick={() => {
                setMenuOpen(false);
                onRemoveFromFolder(session.session_id);
              }}
            >
              <i className="fas fa-folder-minus" />
              <span>Remove from folder</span>
            </button>
          )}

          {/* Delete */}
          <button
            className="menu-item menu-item--danger"
            onClick={() => {
              setMenuOpen(false);
              onDelete(session.session_id);
            }}
          >
            <i className="fas fa-trash" />
            <span>Delete</span>
          </button>

        </div>
      )}

      {/* Folder picker popover — anchored to the ⋯ menu button */}
      <FolderPickerPopover
        isOpen={isFolderPickerOpen}
        anchorRef={menuBtnRef}
        folders={folders}
        onClose={() => setIsFolderPickerOpen(false)}
        onSelect={(folderId) => onMoveToFolder(session.session_id, folderId)}
      />
    </div>
  );
};

export default SessionItem;